"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "motion/react";
import { Check } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";
import { HowItWorksMobile } from "./HowItWorksMobile";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* Shared framer variants: container staggers its children in when active. */
const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] as const } },
};

type ScreenCopy = ReturnType<typeof useTranslation>["t"]["landing"]["cover"]["showcase"]["screens"];

/* ── 01 · Daily Plan ─────────────────────────────────────────────── */
function PlanScreen({ active, copy }: { active: boolean; copy: ScreenCopy["plan"] }) {
  return (
    <div className="h-full w-full px-6 py-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">
          {copy.date}
        </span>
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" />
      </div>
      <h4 className="font-display text-[clamp(18px,1.8vw,24px)] tracking-[-0.01em] text-[var(--ink)]" style={{ fontWeight: 360 }}>
        {copy.title}
      </h4>
      <motion.ul
        variants={listVariants}
        initial="hidden"
        animate={active ? "show" : "hidden"}
        className="flex flex-col gap-2.5 mt-1"
      >
        {copy.tasks.map((task, i) => (
          <motion.li
            key={i}
            variants={rowVariants}
            className="flex items-center gap-3 rounded-[10px] border px-3 py-2.5"
            style={{ borderColor: "var(--hair)", background: "var(--paper)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[var(--terracotta)]" />
            <span className="flex-1 text-[13px] leading-snug text-[var(--ink)] truncate">{task.title}</span>
            <span className="font-mono text-[10px] tracking-[0.1em] text-[var(--ink-soft)]">{task.time}</span>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  );
}

/* ── 02 · Daily Flow ─────────────────────────────────────────────── */
function FlowScreen({ active, copy }: { active: boolean; copy: ScreenCopy["flow"] }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!active) {
      setChecked(false);
      return;
    }
    if (prefersReducedMotion()) {
      setChecked(true);
      return;
    }
    const t = setTimeout(() => setChecked(true), 850);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div className="h-full w-full px-6 py-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">
          {copy.date}
        </span>
        <span className="font-mono text-[10px] tracking-[0.14em] uppercase rounded-full px-2.5 py-1 text-[var(--terracotta)]" style={{ background: "color-mix(in srgb, var(--terracotta) 12%, transparent)" }}>
          {copy.focus}
        </span>
      </div>
      <h4 className="font-display text-[clamp(18px,1.8vw,24px)] tracking-[-0.01em] text-[var(--ink)]" style={{ fontWeight: 360 }}>
        {copy.title}
      </h4>
      <motion.ul
        variants={listVariants}
        initial="hidden"
        animate={active ? "show" : "hidden"}
        className="flex flex-col gap-2.5 mt-1"
      >
        {copy.tasks.map((task, i) => {
          const isChecked = i === 0 && checked;
          return (
            <motion.li
              key={i}
              variants={rowVariants}
              className="flex items-center gap-3 rounded-[10px] border px-3 py-2.5"
              style={{ borderColor: "var(--hair)", background: "var(--paper)" }}
            >
              <span
                className="grid place-items-center w-[18px] h-[18px] rounded-[6px] border shrink-0 transition-colors duration-300"
                style={{
                  borderColor: isChecked ? "var(--moss)" : "var(--rule)",
                  background: isChecked ? "var(--moss)" : "transparent",
                }}
              >
                {isChecked && <Check className="w-3 h-3 text-[var(--paper)]" strokeWidth={3} />}
              </span>
              <span
                className="flex-1 text-[13px] leading-snug truncate transition-all duration-300"
                style={{
                  color: "var(--ink)",
                  textDecoration: isChecked ? "line-through" : "none",
                  opacity: isChecked ? 0.55 : 1,
                }}
              >
                {task.title}
              </span>
              <span className="font-mono text-[10px] tracking-[0.1em] text-[var(--ink-soft)]">{task.time}</span>
            </motion.li>
          );
        })}
      </motion.ul>
    </div>
  );
}

/* ── 03 · Daily Journal ──────────────────────────────────────────── */
function JournalScreen({ active, copy }: { active: boolean; copy: ScreenCopy["journal"] }) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!active) {
      setTyped("");
      return;
    }
    if (prefersReducedMotion()) {
      setTyped(copy.reflection);
      return;
    }
    setTyped("");
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(copy.reflection.slice(0, i));
      if (i >= copy.reflection.length) clearInterval(id);
    }, 28);
    return () => clearInterval(id);
  }, [active, copy.reflection]);

  return (
    <div className="h-full w-full px-6 py-5 flex flex-col gap-4">
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">
        {copy.date}
      </span>
      <h4 className="font-display text-[clamp(18px,1.8vw,24px)] tracking-[-0.01em] text-[var(--ink)]" style={{ fontWeight: 360 }}>
        {copy.title}
      </h4>
      <div
        className="rounded-[10px] border-l-2 px-3.5 py-2.5"
        style={{
          borderColor: "var(--moss)",
          background: "color-mix(in srgb, var(--moss) 8%, transparent)",
        }}
      >
        <p className="font-display italic text-[15px] leading-snug text-[var(--ink)]">{copy.highlight}</p>
      </div>
      <p className="text-[13px] leading-[1.6] text-[var(--ink-soft)]">{copy.body}</p>
      <p className="font-display italic text-[14px] leading-snug text-[var(--ink)] mt-auto">
        {typed}
        {active && typed.length < copy.reflection.length && (
          <span className="inline-block w-[2px] h-[1em] align-middle ml-0.5 bg-[var(--terracotta)] animate-pulse" />
        )}
      </p>
    </div>
  );
}

/* ── 04 · Tomorrow's Preview ─────────────────────────────────────── */
function TomorrowScreen({ active, copy }: { active: boolean; copy: ScreenCopy["tomorrow"] }) {
  return (
    <div className="h-full w-full px-6 py-5 flex flex-col gap-4">
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">
        {copy.date}
      </span>
      <h4 className="font-display text-[clamp(18px,1.8vw,24px)] tracking-[-0.01em] text-[var(--ink)]" style={{ fontWeight: 360 }}>
        {copy.title}
      </h4>
      <motion.ul
        variants={listVariants}
        initial="hidden"
        animate={active ? "show" : "hidden"}
        className="flex flex-col gap-3 mt-1"
      >
        {copy.intentions.map((line, i) => (
          <motion.li key={i} variants={rowVariants} className="flex items-start gap-3">
            <span className="font-mono text-[11px] tracking-[0.1em] text-[var(--terracotta)] mt-1 shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-display italic text-[16px] leading-snug text-[var(--ink)]">{line}</span>
          </motion.li>
        ))}
      </motion.ul>
      <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[var(--ink-soft)] mt-auto">
        {copy.note}
      </p>
    </div>
  );
}

export function PhaseScreen({ index, active, screens }: { index: number; active: boolean; screens: ScreenCopy }) {
  switch (index) {
    case 0:
      return <PlanScreen active={active} copy={screens.plan} />;
    case 1:
      return <FlowScreen active={active} copy={screens.flow} />;
    case 2:
      return <JournalScreen active={active} copy={screens.journal} />;
    default:
      return <TomorrowScreen active={active} copy={screens.tomorrow} />;
  }
}

export function AlignmentLoopShowcase() {
  const { t } = useTranslation();
  const showcase = t.landing.cover.showcase;
  const screens = showcase.screens;
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const idx = Math.min(3, Math.max(0, Math.floor(latest * 4)));
    if (idx !== activeIndex) setActiveIndex(idx);
  });

  const phases = showcase.phases;
  const activePhase = phases[activeIndex] ?? phases[0];

  return (
    <>
      {/* Desktop / tablet: scroll-pinned showcase (≥ lg = 1024px) */}
      <section
        ref={sectionRef}
        className="hidden lg:block relative"
        style={{ height: "400vh" }}
        aria-label={showcase.kicker}
      >
        <div className="sticky top-0 h-screen flex items-center">
          <div className="max-w-[1320px] mx-auto px-6 md:px-12 w-full grid grid-cols-[1fr_1.2fr] gap-16 items-center">
            {/* Left: phase copy */}
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">
                  {showcase.kicker}
                </span>
                <h2
                  className="font-display text-[clamp(30px,3vw,44px)] leading-[1.08] tracking-[-0.02em] text-[var(--ink)] max-w-[15ch]"
                  style={{ fontWeight: 380 }}
                >
                  {showcase.h2}
                </h2>
              </div>
              {/* Fixed height reserves space for the tallest phase so the
                  kicker/h2 above don't shift when phases (of differing copy
                  length) crossfade — fits the longest EN+SL phase. */}
              <div className="min-h-[230px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col gap-4"
                >
                  <span className="font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)]">
                    {activePhase.no}
                  </span>
                  <h3
                    className="font-display text-[clamp(24px,2.4vw,34px)] leading-[1.1] tracking-[-0.02em] text-[var(--ink)]"
                    style={{ fontWeight: 350 }}
                  >
                    {activePhase.title}
                  </h3>
                  <p className="text-[15px] leading-[1.65] text-[var(--ink-soft)] max-w-[460px]">
                    {activePhase.body}
                  </p>
                  {activePhase.cta && (
                    <Link
                      href="/register"
                      className="mt-1 inline-flex w-fit items-center gap-2 rounded-[10px] bg-[var(--terracotta)] px-5 py-2.5 text-[14px] font-medium text-[var(--terracotta-soft)] transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
                    >
                      {activePhase.cta} →
                    </Link>
                  )}
                </motion.div>
              </AnimatePresence>
              </div>

              {/* Progress rail */}
              <div className="flex gap-2 mt-4">
                {phases.map((_, i) => (
                  <div
                    key={i}
                    className="h-[2px] w-12 rounded-full overflow-hidden"
                    style={{ background: "var(--rule)" }}
                  >
                    <motion.div
                      className="h-full"
                      style={{ background: "var(--terracotta)" }}
                      animate={{
                        width: i < activeIndex ? "100%" : i === activeIndex ? "100%" : "0%",
                        opacity: i === activeIndex ? 1 : i < activeIndex ? 0.6 : 0,
                      }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right: device frame with live mock-screen crossfade */}
            <div
              className="relative w-full max-w-[720px] ml-auto rounded-[20px] border overflow-hidden"
              style={{
                aspectRatio: "16 / 10",
                background: "var(--paper-2)",
                borderColor: "var(--hair)",
                boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
              }}
            >
              {/* Top bezel chrome */}
              <div className="absolute top-0 left-0 right-0 h-7 flex items-center px-3 gap-1.5 border-b z-10" style={{ borderColor: "var(--hair)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
              </div>
              <div className="absolute inset-0 pt-7">
                {phases.map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute inset-0 pt-7"
                    style={{ background: "var(--paper)" }}
                    animate={{ opacity: i === activeIndex ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    aria-hidden={i !== activeIndex}
                  >
                    <PhaseScreen index={i} active={i === activeIndex} screens={screens} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore link — desktop only, rendered AFTER the pinned 400vh section so
          it sits cleanly below the demo (in normal flow) instead of scrolling up
          over the still-pinned showcase. */}
      <div className="hidden lg:flex justify-center pb-16 lg:pb-20">
        <Link
          href="/product/daily-rhythm"
          className="inline-flex items-center font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)] underline-offset-4 hover:underline"
        >
          {`${showcase.learnMore} →`}
        </Link>
      </div>

      {/* Mobile (< lg): the pinned horizontal-scroll "How it works" walkthrough. */}
      <HowItWorksMobile />
    </>
  );
}
